import React, { useEffect, useState } from "react";
import { GetConsoleTab } from "../functions/GetConsoleTab";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, getOrganisationAndProjectLogos, getOrganisationLogo, handleUserAuth } from "../firebase/firebase_setup";
import PageLoader from "../loaders/PageLoader";
import '../CSS/private/PageContent_SideBar.css';
import isNarrow from "../functions/isNarrow";
import NoOrganisationLogo from '../media/images/no_organisation_icon.svg';
import NoProjectLogo from '../media/images/no_project_icon.svg';
import AppDrawer from "../components/console_page/drawer/AppDrawer";
import { Box } from "@mui/material";
import NoProjectPageTemplate from "./NoProjectPageTemplate";
import ChangeOrganisationDialog from "../components/dialogs/ChangeOrganisationDialog";
import ChangeProjectDialog from "../components/dialogs/ChangeProjectDialog";
import { Air } from "@mui/icons-material";
import SubTabNoData from "../components/console_page/sub_tab/SubTabNoData";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { AccountSettings } from "../components/console_page/account_settings/AccountSettings";

/**
 * @param {*} props - router              = the console router used for navigating
 *                  - appBar              = the app bar element used at the top of the page
 *                  - accentColour        = the accent colour for the console
 *                  - appName             = the name of the app, according to it's Firebase document
 *                  - consoleName         = the base url of the console (usually 'console' or 'admin-console')
 *                  - admin               = flag to indicate that the provided console is admin-only
 *                  - additionalProps     = any extra props you wish to add to the console (provide in JSON format)
 *                                          these can be accessed in a tab via props.additional.x
 */
function ConsoleTemplate(props) {
  
  // Special prop to restrict access to tabs if there is the user's current show is in an inactive season
  const seasonRestricted = props.season_restricted;
  const [restricted, setRestricted] = useState(null);
  const [seasonName, setSeasonName] = useState('');

  const router = props.router;
  const [tab, setTab] = useState(GetConsoleTab());
  const [user, loading] = useAuthState(auth);
  const [userData, setUserData] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [extraData, setExtraData] = useState(null);
  const [organisationData, setOrganisationData] = useState(null);
  const [narrow, setNarrow] = useState(isNarrow());

  // Control flags for displaying the change organisation and change project dialogs
  const [organisationDialogOpen, setOrganisationDialogOpen] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);

  // Control flag for account settings
  const [settingsOpen, setSettingsOpen] = useState(false);


  window.addEventListener("resize", function(event) {
    setNarrow(isNarrow());
  })

  useEffect(() => {
    if(loading) return;
    handleUserAuth(setUserData, setProjectData, setExtraData, setOrganisationData, props.appName, (props.admin? props.admin : false));
    // eslint-disable-next-line
  }, [user, loading]);

  // Check if the user's current season is inactive - set restricted to true if so.
  useEffect(() => {
    if(!seasonRestricted) setRestricted(false);
    else if(organisationData === false) {
      setRestricted(false);
    }
    else if(organisationData !== null && (restricted === null)) {
      const projectSeason = projectData.season;
      let restrict = false;
      let season = '';
      organisationData.seasons.forEach((s) => {
        if(s.uid === projectSeason) {
          season = s.name;
          if(s.active === false) restrict = true;
        }
      });
      setSeasonName(season);
      setRestricted(restrict);
    }
  }, [projectData, organisationData])

  /*
  *  liveTab is a lighter, stateless version of the 'tab' state.
  *  As the 'setTab' call takes time to propogate, it will cause crashes
  *  in the rare edge cases that need to change the console's router mid-session
  *  (e.g. CastingBot's ClashBot system).
  *  liveTab doesn't suffer from this issue, as it is updated with every state change made to the
  *  console.
  */
  const liveTab = (tab in router? tab : Object.keys(router)[0]);

  // If the tab is invalid, default to the first tab in the list
  if(tab in router === false) {
    setTab(Object.keys(router)[0]);
    window.history.pushState(null, '', ('/' + (props.consoleName) + '/' + Object.keys(router)[0]));
  }

  console.log(restricted, userData, projectData, extraData, organisationData);
  if(restricted === null || userData === null || projectData === null || extraData === null || organisationData === null) return <PageLoader />

  // Create a clone of the provided header, and give it the User and Project data, as well as functions for opening organisation and project dialogs
  const clonedAppBar = React.cloneElement( props.appBar, { 
    userData: userData, 
    projectData: projectData, 
    setOrganisationDialogOpen: setOrganisationDialogOpen, 
    setProjectDialogOpen: setProjectDialogOpen, 
    setSettingsOpen: setSettingsOpen 
  });


  if(restricted === true) {
    return (
      <>
        {
          settingsOpen && 
          <AccountSettings userData={userData} setSettingsOpen={setSettingsOpen} accentColour={props.accentColour} />
        }
        <div>
          <Box sx={{ display: 'flex' }}>
            {clonedAppBar}
          </Box>
          <div>
            <SubTabNoData 
              icon={faExclamationTriangle} 
              title='Access Restricted' 
              description={`The ${seasonName} season is no longer active.`}
            />
          </div>
          <ChangeOrganisationDialog userData={userData} open={organisationDialogOpen} handleClose={() => {setOrganisationDialogOpen(false)}} />
          <ChangeProjectDialog userData={userData} open={projectDialogOpen} handleClose={() => {setProjectDialogOpen(false)}} />
        </div>
      </>
    )
  }


  // A data object that passes additional props into the tabs
  const passData = {
    userData: userData,
    setUserData: setUserData,
    projectData: projectData,
    setProjectData: setProjectData,
    extraData: extraData,
    setExtraData: setExtraData,
    organisationData: organisationData,
    setOrganisationData: setOrganisationData,
    narrow: narrow,
    accentColour: props.accentColour,
    additional: props.additionalProps
  }

  // Create a clone of the provided tab content, and give it the user, project, and extra data
  const clonedTab = React.cloneElement( router[liveTab].element, { ...{passData} });

  return (
    <>
      {
        settingsOpen && 
        <AccountSettings userData={userData} setSettingsOpen={setSettingsOpen} accentColour={props.accentColour} />
      }
      <div>
        {
          projectData !== false? (
            <Box sx={{ display: 'flex' }}>
              {clonedAppBar}
              <AppDrawer 
                consoleName={props.consoleName} 
                router={router} 
                admin={props.admin}
                tab={liveTab} 
                setTab={setTab} 
                userData={userData}
                projectData={projectData}
                organisationData={organisationData}
                accentColour={props.accentColour} 
                narrow={narrow} 
              />
              <div className={'slp-pagecontent' + (narrow? '-narrow' : '')}>
                <h1 className='slp-pagecontent-title'>{router[liveTab].title}</h1>
                {clonedTab}
              </div>
            </Box>
          ) : (
            <NoProjectPageTemplate appBar={clonedAppBar} userData={userData} organisationData={organisationData} />
          )
        }
        <ChangeOrganisationDialog userData={userData} open={organisationDialogOpen} handleClose={() => {setOrganisationDialogOpen(false)}} />
        <ChangeProjectDialog userData={userData} open={projectDialogOpen} handleClose={() => {setProjectDialogOpen(false)}} />
      </div>
    </>
  )

}

export default ConsoleTemplate;